import { useState } from 'react';
import { Mail, Phone, MapPin, ArrowRight, MessageCircle, CalendarDays, Compass } from 'lucide-react';

const NEXT_STEPS = [
  { icon: CalendarDays, title: 'Book a call', body: 'Pick a 30-minute slot that works for you.' },
  { icon: Compass, title: 'We understand your goals', body: "A conversation, not a form. We start where you are." },
  { icon: MessageCircle, title: 'You get a personalised plan', body: 'Programs, costs, timelines — mapped to your life.' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          program: form.interest, notes: form.notes, source: 'website_contact',
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch (e2) {
      setErr('Something went wrong. Please try again or email us directly.');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-brand-alabaster font-sans-clean text-brand-obsidian" data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-4">Get in touch</div>
            <h1 className="font-serif-editorial text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Book a free <span className="text-brand-pine italic">consultation.</span>
            </h1>
            <p className="mt-8 text-lg text-brand-slate leading-relaxed">
              Choosing the right education is a big decision. Talk to an advisor — no pressure, no cost.
            </p>

            <div className="mt-12 space-y-4">
              <div className="flex items-start gap-3 text-sm">
                <Mail size={16} className="text-brand-coral mt-0.5 flex-shrink-0" />
                <a href="mailto:info@trueglobaleducation.com" className="hover:text-brand-pine transition-colors">info@trueglobaleducation.com</a>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Phone size={16} className="text-brand-coral mt-0.5 flex-shrink-0" />
                <a href="tel:+16048141515" className="hover:text-brand-pine transition-colors">(604) 814-1515</a>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin size={16} className="text-brand-coral mt-0.5 flex-shrink-0" />
                <span>Payal Business Centre<br />8166 128 St #109, Surrey, BC V3W 4G1</span>
              </div>
              <div className="pt-4 border-t border-brand-border/40">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-3">Office hours</div>
                <div className="text-sm text-brand-slate space-y-1">
                  <div className="flex justify-between gap-6"><span>Monday – Friday</span><span>8:00 a.m. – 5:00 p.m.</span></div>
                  <div className="flex justify-between gap-6"><span>Saturday</span><span>9:00 a.m. – 4:00 p.m.</span></div>
                  <div className="flex justify-between gap-6"><span>Sunday</span><span>Closed</span></div>
                </div>
              </div>
            </div>

            <div className="mt-14">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-6">What happens next</div>
              <div className="space-y-6">
                {NEXT_STEPS.map((s, i) => (
                  <div key={s.title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-pine/5 flex items-center justify-center flex-shrink-0">
                      <s.icon size={16} className="text-brand-pine" />
                    </div>
                    <div>
                      <div className="font-serif-editorial text-lg leading-snug">{i + 1}. {s.title}</div>
                      <p className="text-sm text-brand-slate mt-1 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl border border-brand-border/50 p-8 md:p-10 shadow-sm">
              {submitted ? (
                <div className="py-12 text-center" data-testid="contact-success">
                  <div className="w-14 h-14 mx-auto rounded-full bg-brand-pine/10 flex items-center justify-center">
                    <ArrowRight size={20} className="text-brand-pine" />
                  </div>
                  <h3 className="font-serif-editorial text-2xl mt-6 leading-snug">We&apos;ve got your details.</h3>
                  <p className="text-brand-slate text-sm mt-3 max-w-md mx-auto leading-relaxed">
                    An advisor will reach out within one business day to schedule your free consultation.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-obsidian/60 mb-2 block">Your name</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full border border-brand-border/60 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                      data-testid="contact-name" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-brand-obsidian/60 mb-2 block">Email</label>
                      <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full border border-brand-border/60 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                        data-testid="contact-email" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-brand-obsidian/60 mb-2 block">Phone</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full border border-brand-border/60 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                        data-testid="contact-phone" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-obsidian/60 mb-2 block">What do you want to explore?</label>
                    <input value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}
                      placeholder="e.g. Healthcare, career change, part-time programs…"
                      className="w-full border border-brand-border/60 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                      data-testid="contact-interest" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-obsidian/60 mb-2 block">Anything else? (optional)</label>
                    <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full border border-brand-border/60 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-pine transition-colors"
                      data-testid="contact-notes" />
                  </div>
                  {err && <div className="text-sm text-red-600" data-testid="contact-error">{err}</div>}
                  <button type="submit" disabled={loading}
                    className="inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-8 py-4 rounded-full transition-colors disabled:opacity-60"
                    data-testid="contact-submit">
                    {loading ? 'Sending…' : 'Book my consultation'} <ArrowRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
